/**
 * AppearanceProvider — pulls the clinic's appearance preferences from
 * `/api/settings/appearance` on mount, writes them to `<html>` as CSS
 * variables + data-attributes, and exposes a small hook so any settings UI
 * can update the live document + persist to the backend.
 *
 * All controls preview instantly by mutating `document.documentElement`
 * before the API round-trip completes.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const DEFAULTS = {
  mode: "light",
  primary: "#F5532D",       // Viyora healthcare orange
  preset: "saffron",        // premium orange preset for new clinics
  radius: 16,
  density: "comfortable",
  font: "Inter",
  animations: true,
  text_size: "medium",
  high_contrast: false,
  reduce_motion: false,
  sidebar: "solid",
  chart: "modern",
  card: "elevated",
  toast: "modern",
};

const TEXT_SCALE = { small: 0.94, medium: 1, large: 1.1 };

const CACHE_KEY = "viyora:appearance";

/**
 * Apply the appearance object to <html>. Runs on every mount + on every
 * update so previewing a change is instant.
 */
function apply(a) {
  const html = document.documentElement;
  html.style.setProperty("--v-primary", a.primary);
  html.style.setProperty("--v-radius", `${a.radius}px`);
  html.style.setProperty("--v-font", `"${a.font}", ui-sans-serif, system-ui, sans-serif`);
  html.style.setProperty("--v-text-scale", String(TEXT_SCALE[a.text_size] || 1));
  html.style.setProperty("--v-motion", a.animations ? "1" : "0");

  // Resolve system mode -> actual mode
  const effectiveMode = a.mode === "system"
    ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
    : a.mode;
  html.setAttribute("data-theme", effectiveMode);
  html.setAttribute("data-density", a.density);
  html.setAttribute("data-hc", a.high_contrast ? "1" : "0");
  html.setAttribute("data-reduce-motion", (a.reduce_motion || !a.animations) ? "1" : "0");
  html.setAttribute("data-sidebar", a.sidebar || "solid");
  html.setAttribute("data-chart",   a.chart   || "modern");
  html.setAttribute("data-card",    a.card    || "elevated");
  html.setAttribute("data-toast",   a.toast   || "modern");
  // Per-preset override drives the compatibility CSS in index.css so clinics
  // that had chosen forest / midnight / luxury etc. keep seeing their theme.
  html.setAttribute("data-preset",  a.preset  || "saffron");
}


const AppearanceCtx = createContext({
  appearance: DEFAULTS,
  loading: true,
  update: async () => {},
  reset: async () => {},
});


export function AppearanceProvider({ children }) {
  // Read cached settings synchronously so the very first paint is themed —
  // avoids the classic "flash of default theme".
  const [appearance, setAppearance] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      if (cached) { apply({ ...DEFAULTS, ...cached }); return { ...DEFAULTS, ...cached }; }
    } catch { /* ignore */ }
    apply(DEFAULTS);
    return DEFAULTS;
  });
  const [loading, setLoading] = useState(true);

  // Fetch fresh from the server on mount (once we have an auth token).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings/appearance");
        if (cancelled) return;
        setAppearance(data);
        apply(data);
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      } catch { /* not logged in yet — cached defaults are fine */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Watch OS theme flips when mode="system"
  useEffect(() => {
    if (appearance.mode !== "system") return;
    const mm = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => apply(appearance);
    mm.addEventListener("change", onChange);
    return () => mm.removeEventListener("change", onChange);
  }, [appearance]);

  const update = useCallback(async (patch) => {
    // Optimistic apply — user sees change instantly
    const next = { ...appearance, ...patch };
    setAppearance(next);
    apply(next);
    localStorage.setItem(CACHE_KEY, JSON.stringify(next));
    try {
      const { data } = await api.put("/settings/appearance", patch);
      setAppearance(data);
      apply(data);
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (err) {
      // Roll back on failure
      setAppearance(appearance);
      apply(appearance);
      throw err;
    }
  }, [appearance]);

  const reset = useCallback(async () => {
    const { data } = await api.post("/settings/appearance/reset");
    setAppearance(data);
    apply(data);
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  }, []);

  const value = useMemo(() => ({ appearance, loading, update, reset }), [appearance, loading, update, reset]);
  return <AppearanceCtx.Provider value={value}>{children}</AppearanceCtx.Provider>;
}


export function useAppearance() { return useContext(AppearanceCtx); }
